import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Edit2, Trash2, Eye, Play, Pause, RefreshCw, Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import { Banner } from '../../../components/common/Banner';
import { getAllPopups, createPopup, updatePopup, deletePopup, rotatePopups, getActivePopup, resetLoop } from '../../../services/popupsService';
import PopupModal from '../../../components/common/PopupModal';

export default function PopupsAdminPage() {
  const navigate = useNavigate();
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [activePopup, setActivePopup] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    file: null,
  });

  useEffect(() => {
    loadPopups();
    loadActivePopup();
  }, []);

  const loadPopups = async () => {
    try {
      setLoading(true);
      const data = await getAllPopups();
      // Ordenar por posición
      const sorted = data.sort((a, b) => (a.position || 0) - (b.position || 0));
      setPopups(sorted);
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al cargar pop-ups', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadActivePopup = async () => {
    try {
      const popup = await getActivePopup();
      setActivePopup(popup);
    } catch (error) {
      // Silencioso
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        Swal.fire('Error', 'Por favor, selecciona un archivo de imagen (PNG, JPG, JPEG, etc.)', 'error');
        return;
      }
      // Validar tamaño (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('Error', 'El archivo es demasiado grande. Máximo 10MB', 'error');
        return;
      }
      setFormData({ ...formData, file });
    }
  };

  const handleUpload = async () => {
    if (!formData.file) {
      Swal.fire('Error', 'Por favor, selecciona una imagen', 'error');
      return;
    }

    try {
      setUploading(true);
      await createPopup(formData.file, {
        title: formData.title || undefined,
        link: formData.link || undefined,
      });

      await Swal.fire({
        icon: 'success',
        title: 'Pop-up creado',
        text: 'La imagen se subió correctamente a Cloudinary',
        timer: 2000,
        showConfirmButton: false,
      });

      setShowUploadModal(false);
      setFormData({ title: '', link: '', file: null });
      await loadPopups();
      await loadActivePopup();
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al crear pop-up', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (popup) => {
    const result = await Swal.fire({
      title: '¿Eliminar pop-up?',
      text: `¿Estás seguro de eliminar "${popup.title || 'este pop-up'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deletePopup(popup.id);
        await Swal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: 'Pop-up eliminado correctamente',
          timer: 1500,
          showConfirmButton: false,
        });
        await loadPopups();
        await loadActivePopup();
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al eliminar pop-up', 'error');
      }
    }
  };

  const handleToggleActive = async (popup) => {
    try {
      await updatePopup(popup.id, { isActive: !popup.isActive });
      await loadPopups();
      await loadActivePopup();
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al actualizar pop-up', 'error');
    }
  };

  const handleRotate = async () => {
    const result = await Swal.fire({
      title: '¿Ejecutar rotación?',
      text: 'Esto activará el siguiente pop-up en la cola',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, rotar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await rotatePopups();
        await Swal.fire({
          icon: 'success',
          title: 'Rotación ejecutada',
          timer: 1500,
          showConfirmButton: false,
        });
        await loadPopups();
        await loadActivePopup();
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al rotar pop-ups', 'error');
      }
    }
  };

  const handleResetLoop = async () => {
    const result = await Swal.fire({
      title: '¿Reiniciar loop?',
      text: 'Esto reiniciará el ciclo desde el primer pop-up y reprogramará todos',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, reiniciar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await resetLoop();
        await Swal.fire({
          icon: 'success',
          title: 'Loop reiniciado',
          text: 'El ciclo de pop-ups se reinició desde el principio',
          timer: 2000,
          showConfirmButton: false,
        });
        await loadPopups();
        await loadActivePopup();
      } catch (error) {
        Swal.fire('Error', error.message || 'Error al reiniciar loop', 'error');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Banner
        title="Administración de Pop-ups"
        subtitle="Gestiona los pop-ups que se muestran a los administradores"
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header con botones */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#09274A] hover:text-[#0488AF] transition-colors"
          >
            <ArrowLeft size={20} />
            Volver
          </button>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleResetLoop}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              title="Reiniciar el ciclo desde el primer pop-up"
            >
              <RefreshCw size={18} />
              Reiniciar Loop
            </button>
            <button
              onClick={handleRotate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw size={18} />
              Rotar Pop-ups
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#09274A] text-white rounded-lg hover:bg-[#0488AF] transition-colors"
            >
              <Plus size={18} />
              Nuevo Pop-up
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#09274A]"></div>
          </div>
        )}

        {/* Lista de pop-ups */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popups.map((popup) => (
              <div
                key={popup.id}
                className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                  popup.isActive ? 'border-green-500' : 'border-gray-200'
                }`}
              >
                {/* Imagen */}
                <div className="relative h-48 bg-gray-100">
                  <img
                    src={popup.imageUrl}
                    alt={popup.title || 'Pop-up'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  {popup.isActive && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                      ACTIVO
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 truncate">
                    {popup.title || 'Sin título'}
                  </h3>
                  {popup.link && (
                    <a
                      href={popup.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline truncate block"
                    >
                      {popup.link}
                    </a>
                  )}
                  <div className="mt-3 text-xs text-gray-500 space-y-1">
                    <div>Inicio: {formatDate(popup.startDate)}</div>
                    <div>Fin: {formatDate(popup.endDate)}</div>
                    <div>Posición: {popup.position || 0}</div>
                  </div>

                  {/* Botones */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setSelectedPopup(popup);
                        setShowPreview(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                      title="Ver preview"
                    >
                      <Eye size={16} />
                      Ver
                    </button>
                    <button
                      onClick={() => handleToggleActive(popup)}
                      className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded text-sm transition-colors ${
                        popup.isActive
                          ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800'
                          : 'bg-green-100 hover:bg-green-200 text-green-800'
                      }`}
                      title={popup.isActive ? 'Desactivar' : 'Activar'}
                    >
                      {popup.isActive ? <Pause size={16} /> : <Play size={16} />}
                      {popup.isActive ? 'Pausar' : 'Activar'}
                    </button>
                    <button
                      onClick={() => handleDelete(popup)}
                      className="flex items-center justify-center gap-1 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded text-sm transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {popups.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                No hay pop-ups creados. Haz clic en "Nuevo Pop-up" para crear uno.
              </div>
            )}
          </div>
        )}

        {/* Modal de subida */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold mb-4 text-[#09274A]">Subir Nuevo Pop-up</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Imagen (PNG, JPG, JPEG) *
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos recomendados: PNG o JPG desde Canva. Máximo 10MB.
                  </p>
                  {formData.file && (
                    <div className="mt-2 text-sm text-green-600">
                      ✓ {formData.file.name}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título (opcional)
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                    placeholder="Ej: Promoción especial"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enlace al hacer clic (opcional)
                  </label>
                  <input
                    type="url"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#09274A]"
                    placeholder="https://ejemplo.com"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setFormData({ title: '', link: '', file: null });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                  disabled={uploading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !formData.file}
                  className="flex-1 px-4 py-2 bg-[#09274A] hover:bg-[#0488AF] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Subir a Cloudinary
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de preview */}
        {showPreview && selectedPopup && (
          <PopupModal
            popup={selectedPopup}
            onClose={() => {
              setShowPreview(false);
              setSelectedPopup(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

